import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { mailDispositionSchema } from '../enums/mail-disposition.enum.js'

/**
 * One recorded attempt to email a proposal version to a client.
 *
 * The answer to "did this actually go, and has anyone opened it" — a question
 * that had no answer while sending was a human asserting they had done it by
 * hand.
 */
export const proposalDeliveryDtoSchema = z.object({
  id: uuidSchema,
  versionId: uuidSchema,
  /** Where it went, as captured at send time rather than read back now. */
  toEmail: z.string(),
  toName: z.string().nullable(),
  disposition: mailDispositionSchema,
  /**
   * When the recipient first opened the link, and last did. Null means nobody
   * has followed it — which is weaker than "not received", and is shown as the
   * weaker claim.
   */
  firstViewedAt: z.string().datetime().nullable(),
  lastViewedAt: z.string().datetime().nullable(),
  viewCount: z.number().int().nonnegative(),
  revokedAt: z.string().datetime().nullable(),
  sentByName: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type ProposalDeliveryDto = z.infer<typeof proposalDeliveryDtoSchema>

/**
 * Who a proposal would be emailed to, and whether it can be.
 *
 * Resolved from the client's primary contact. Surfaced before sending rather
 * than discovered on failure: "there is nobody to send this to" is something
 * to learn while looking at the proposal, not from an error after committing
 * to freeze it.
 */
export const proposalRecipientDtoSchema = z.object({
  name: z.string(),
  title: z.string().nullable(),
  /** Null when the primary contact has no address recorded. */
  email: z.string().nullable(),
})

export type ProposalRecipientDto = z.infer<typeof proposalRecipientDtoSchema>
